<script setup>
// [xleaders] XO-06 — mở đúng hội thoại của khách trên hộp thư Meta của Page, giữ lại thao tác
// "click tên khách xem profile Facebook" mà sale vẫn làm trước khi chuyển sang Chatwoot.
//
// Link nạp SẴN lúc mở hội thoại chứ không nạp lúc bấm: `window.open` phải chạy đồng bộ trong sự
// kiện click, nếu chờ request xong mới gọi thì trình duyệt coi là popup và chặn.
import { computed, ref, watch, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import messengerLink from 'dashboard/api/xleaders/messengerLink';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  conversationId: { type: [String, Number], default: null },
  channelType: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const { t } = useI18n();

// Đủ ba mã controller trả về. Thiếu một mã là sale thấy nút tắt mà không biết vì sao.
const ERROR_KEYS = {
  thread_not_found: 'CONTACT_PANEL.META_INBOX.THREAD_NOT_FOUND',
  graph_error: 'CONTACT_PANEL.META_INBOX.GRAPH_ERROR',
  not_a_messenger_conversation: 'CONTACT_PANEL.META_INBOX.NOT_MESSENGER',
};

const url = ref('');
const errorKey = ref('');
const isLoading = ref(false);
// Số thứ tự lượt nạp mới nhất. Graph trả chậm không đều, nên response của hội thoại cũ hoàn toàn có
// thể về SAU khi sale đã chuyển sang khách khác — ghi đè thì nút mở nhầm hộp thư của người trước mà
// không có dấu hiệu gì. Chỉ nhận kết quả của lượt mới nhất.
//
// Đếm lượt chứ KHÔNG mốc theo `conversationId`: mốc theo id thì đường A→B→A nhận cả response A cũ
// lẫn A mới, hai lượt ghi đè lẫn nhau và tooltip báo lỗi trong khi nút vẫn mở được.
let requestSeq = 0;

const shouldRender = computed(
  () => props.channelType === INBOX_TYPES.FB && !!props.conversationId
);

const tooltip = computed(() => {
  if (errorKey.value) return t(errorKey.value);
  if (isLoading.value) return t('CONTACT_PANEL.META_INBOX.LOADING');
  return t('CONTACT_PANEL.META_INBOX.BUTTON');
});

const fetchLink = async () => {
  url.value = '';
  errorKey.value = '';
  // Tăng mốc trước cả lần return sớm cho nhất quán: lượt đang bay bị vô hiệu hoá kể cả khi sale
  // nhảy sang kênh không có nút. (Chưa quan sát được từ UI vì mọi đường quay lại đều chạy hàm này
  // và xoá `url` trước — nhưng đừng dời xuống dưới, nó là thứ giữ cho bất biến "chỉ lượt mới nhất
  // được ghi" đúng ở MỌI đường, không phụ thuộc vào việc `url` có được xoá hay không.)
  requestSeq += 1;
  const seq = requestSeq;
  if (!shouldRender.value) return;

  isLoading.value = true;
  try {
    const { data } = await messengerLink.show(
      props.accountId,
      props.conversationId
    );
    if (seq !== requestSeq) return;
    url.value = data.url;
  } catch (error) {
    if (seq !== requestSeq) return;
    // Controller trả { error: '<mã>' }; axios chỉ để lại "Request failed with status code …" ở
    // error.message nên phải bóc từ response mới ra lý do thật.
    const code = error?.response?.data?.error;
    errorKey.value = ERROR_KEYS[code] || ERROR_KEYS.graph_error;
  } finally {
    if (seq === requestSeq) isLoading.value = false;
  }
};

watch(() => [props.conversationId, props.channelType], fetchLink, {
  immediate: true,
});

// Nút KHÔNG bị `disabled` khi hỏng: trình duyệt không bắn sự kiện chuột trên form control disabled
// nên tooltip lý do sẽ không bao giờ hiện ra — sale chỉ thấy một nút xám câm. Bấm vào thì nói rõ lý
// do rồi thử lại luôn, nhờ đó lỗi mạng thoáng qua không khoá nút suốt thời gian còn ở hội thoại này.
const onClick = () => {
  if (url.value) {
    window.open(url.value, '_blank', 'noopener');
    return;
  }
  if (isLoading.value) return;

  useAlert(tooltip.value);
  fetchLink();
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltip"
      v-bind="attrs"
      icon="i-ph-messenger-logo"
      :is-loading="isLoading"
      :disabled="isLoading"
      @click="onClick"
    />
  </span>
</template>
